/**
 * Quick actions on a ride (docs/RIDES_UX_PLAN.md §3.7): a "More actions"
 * button on ride cards, the latest-ride card and calendar badges, shown on
 * hover and keyboard focus (always on touch screens), with Analyze ride /
 * Open analysis, Ask coach, Rename and Delete. Activities get Rename and
 * Delete only.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDeleteRide, useRenameRide } from '../hooks/useRides.ts';
import { usePlan } from '../hooks/usePlan.ts';
import { formatRideDateShort } from '../lib/rideFormat.ts';
import { askCoachPath, rideActionsFor, type RideAction } from '../lib/ridesHome.ts';
import { useStore } from '../lib/store.ts';
import type { RideListItem } from '../types/api.ts';
import { LucideIcon } from './icons/LucideIcon.tsx';

const MAX_NAME = 100;

/**
 * ``children`` with the "More actions" button over its corner: bottom
 * right on cards, a smaller one top right on calendar badges.
 */
export function WithRideActions({
  ride,
  children,
  small = false,
}: Readonly<{ ride: RideListItem; children: ReactNode; small?: boolean }>) {
  return (
    <div className="relative group">
      {children}
      <div
        className={`absolute ${small ? 'top-0.5 right-0.5' : 'bottom-2 right-2'} opacity-0 group-hover:opacity-100 focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity`}
      >
        <RideActionsMenu ride={ride} small={small} />
      </div>
    </div>
  );
}

type Dialog = 'rename' | 'delete' | null;

export function RideActionsMenu({ ride, small = false }: Readonly<{ ride: RideListItem; small?: boolean }>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const button = useRef<HTMLButtonElement>(null);
  const wrap = useRef<HTMLSpanElement>(null);
  const menuId = useId();
  const run = useRunAction(ride, setDialog);

  const close = useCallback(() => {
    setOpen(false);
    button.current?.focus();
  }, []);

  return (
    <span ref={wrap} className="relative inline-block">
      <button
        ref={button}
        type="button"
        aria-label={t('rideActions.more')}
        title={t('rideActions.more')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`rounded-full border border-(--color-border) bg-(--color-bg-tertiary) text-(--color-text-secondary) hover:text-(--color-text-primary) flex items-center justify-center ${small ? 'h-5 w-5' : 'h-8 w-8'}`}
      >
        <LucideIcon name="ellipsis" size={small ? 12 : 16} />
      </button>
      {open && (
        <ActionsList
          id={menuId}
          ride={ride}
          wrap={wrap}
          onClose={close}
          onPick={(action) => {
            setOpen(false);
            run(action);
          }}
        />
      )}
      {dialog === 'rename' && <RenameDialog ride={ride} onClose={() => setDialog(null)} />}
      {dialog === 'delete' && <DeleteDialog ride={ride} onClose={() => setDialog(null)} />}
    </span>
  );
}

function useRunAction(ride: RideListItem, setDialog: (d: Dialog) => void) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (action: RideAction) => {
    switch (action) {
      case 'analyze':
        navigate(`/rides/${ride.id}?analyze=1`);
        break;
      case 'openAnalysis':
        navigate(`/rides/${ride.id}`);
        break;
      case 'askCoach':
        navigate(
          askCoachPath(
            t('rideActions.askPrompt', { date: formatRideDateShort(ride.started_at), name: ride.name ?? 'Ride' }),
          ),
        );
        break;
      default:
        setDialog(action);
    }
  };
}

function ActionsList({
  id,
  ride,
  wrap,
  onClose,
  onPick,
}: Readonly<{
  id: string;
  ride: RideListItem;
  wrap: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  onPick: (a: RideAction) => void;
}>) {
  const { t } = useTranslation();
  const list = useRef<HTMLDivElement>(null);
  useMenuKeys(list, wrap, onClose);
  return (
    <div
      ref={list}
      id={id}
      role="menu"
      aria-label={t('rideActions.more')}
      tabIndex={-1}
      className="absolute right-0 z-30 mt-1 min-w-56 rounded-lg border border-(--color-border) bg-(--color-bg-secondary) shadow-lg py-1 flex flex-col"
    >
      {rideActionsFor(ride).map((action) => (
        <button
          key={action}
          type="button"
          role="menuitem"
          onClick={() => onPick(action)}
          className={`text-left px-3 py-2 text-sm hover:bg-(--color-bg-tertiary) focus:bg-(--color-bg-tertiary) outline-none ${action === 'delete' ? 'text-red-400' : 'text-(--color-text-primary)'}`}
        >
          {t(`rideActions.${action}`)}
          {action === 'askCoach' && <FreeMessagesNote />}
        </button>
      ))}
    </div>
  );
}

/** Focus the first item; Escape and a click outside close the menu. */
function useMenuKeys(
  list: React.RefObject<HTMLDivElement | null>,
  wrap: React.RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  useEffect(() => {
    list.current?.querySelector<HTMLButtonElement>('[role="menuitem"]')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) onClose();
    };
    globalThis.addEventListener('keydown', onKey);
    globalThis.addEventListener('mousedown', onDown);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
      globalThis.removeEventListener('mousedown', onDown);
    };
  }, [list, wrap, onClose]);
}

/** "Free coach messages this month: 3 of 5" under Ask coach (free only). */
function FreeMessagesNote() {
  const { t } = useTranslation();
  const { isFree, freeChatLeft, freeChatMonthly } = usePlan();
  if (!isFree || freeChatLeft == null || freeChatMonthly == null) return null;
  return (
    <span className="block text-xs text-(--color-text-secondary)">
      {t('plan.freeLeft', { left: freeChatLeft, total: freeChatMonthly })}
    </span>
  );
}

function DialogFrame({
  title,
  children,
  onClose,
}: Readonly<{ title: string; children: ReactNode; onClose: () => void }>) {
  const titleId = useId();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [onClose]);
  // On <body>: the button's corner fades out when the pointer leaves it.
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <dialog
        open
        aria-modal="true"
        aria-labelledby={titleId}
        className="static m-0 w-full max-w-sm rounded-2xl border border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) p-5 flex flex-col gap-4"
      >
        <h2 id={titleId} className="text-lg font-semibold">{title}</h2>
        {children}
      </dialog>
    </div>,
    document.body,
  );
}

function useResultToast() {
  const addToast = useStore((s) => s.addToast);
  return (message: string, ok: boolean) => addToast({ message, type: ok ? 'success' : 'error' });
}

const BUTTON = 'px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50';
const CANCEL = `${BUTTON} bg-(--color-bg-tertiary) text-(--color-text-primary) border border-(--color-border)`;

function RenameDialog({ ride, onClose }: Readonly<{ ride: RideListItem; onClose: () => void }>) {
  const { t } = useTranslation();
  const [name, setName] = useState(ride.name ?? '');
  const rename = useRenameRide();
  const toast = useResultToast();
  const value = name.trim();

  const save = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (!value) return;
    try {
      await rename.mutateAsync({ id: ride.id, name: value });
      toast(t('rideActions.renamed'), true);
    } catch {
      toast(t('rideActions.failed'), false);
    }
    onClose();
  };

  return (
    <DialogFrame title={t('rideActions.renameTitle')} onClose={onClose}>
      <form onSubmit={save} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-(--color-text-secondary)">{t('rideActions.renameLabel')}</span>
          <input
            autoFocus
            value={name}
            maxLength={MAX_NAME}
            onChange={(e) => setName(e.target.value)}
            className="rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-2 text-(--color-text-primary)"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={CANCEL}>
            {t('rideActions.cancel')}
          </button>
          <button type="submit" disabled={!value || rename.isPending} className={`${BUTTON} bg-green-400 text-gray-900`}>
            {t('rideActions.save')}
          </button>
        </div>
      </form>
    </DialogFrame>
  );
}

function DeleteDialog({ ride, onClose }: Readonly<{ ride: RideListItem; onClose: () => void }>) {
  const { t } = useTranslation();
  const remove = useDeleteRide();
  const toast = useResultToast();

  const confirm = async () => {
    try {
      await remove.mutateAsync(ride.id);
      toast(t('rideActions.deleted'), true);
    } catch {
      toast(t('rideActions.failed'), false);
    }
    onClose();
  };

  return (
    <DialogFrame title={t('rideActions.deleteTitle')} onClose={onClose}>
      <p className="text-sm text-(--color-text-secondary)">{t('rideActions.deleteBody')}</p>
      <div className="flex justify-end gap-2">
        <button type="button" autoFocus onClick={onClose} className={CANCEL}>
          {t('rideActions.cancel')}
        </button>
        <button
          type="button"
          disabled={remove.isPending}
          onClick={() => void confirm()}
          className={`${BUTTON} bg-red-500 text-white`}
        >
          {t('rideActions.delete')}
        </button>
      </div>
    </DialogFrame>
  );
}
